<template>
    <div
        id="app"
        v-if="currentBrowser.name === 'IE' || currentBrowser.name === 'Edge' || currentBrowser.name === 'Safari' && currentBrowser.version < 10"
        class="notranslate"
    >
        <unsupportedPage />
    </div>
    <div
        v-else-if="state.global.isRefreshed === false"
        data-background
        id="app"
        :data-layout-not-loaded="!state.layout"
        :data-background-fill="state.layout && state.layout.BackgroundImageFill"
        :data-next-button-clicked="state.global.nextButtonClicked"
        class="notranslate"
    >
        <transition name="fade">
            <router-view />
        </transition>
        <div v-if="errorMessage !== ``" role="alert" id="error-message">
            <strong>{{ errorMessage }}</strong>
        </div>
        <loader v-if="state.global.showLoader" />
        <guidpocket v-if="isDeveloper" />
    </div>
</template>
<script>
import methods from "@/global/methods.js";
import loader from "@/components/Loader";
import guidpocket from "@/components/GuidPocket";
import unsupportedPage from "@/components/Unsupportedpage.vue";
import apivalidation from "@/global/apivalidation.js";

export default {
    name: `app`,
    components: {
        loader,
        guidpocket,
        unsupportedPage,
    },
    data: function () {
        return {
            loginTypesThatRequireLoginScreen: [`EmailEntry`, `CodeEntry`, `EmailList`, `CodeList`, `EmailSignIn`, `Persgroep`],
        };
    },
    computed: {
        state: function () {
            return this.$store.state;
        },
        surveyInfo: function () {
            return this.state.surveyInfo;
        },
        errorMessage: function () {
            return this.state.global.errorMessage;
        },
        isDeveloper: function () {
            let host = location.host;
            return host === `ctsurvey.crowdtech-dev.com` || host === `ctsurvey.crowdtech-acceptance.com`;
        },
        currentBrowser: function () {
            return methods.getBrowser();
        },
    },
    methods: {
        // Get the relevant information of this survey form the server so we can decide what has to happen
        getLoginMethod() {
            let queryParams = this.state.surveyInfo.queryParams;
            let data = {
                surveyId: this.surveyInfo.surveyId,
            };

            if (queryParams && Object.keys(queryParams).length > 0) data.queryParameters = queryParams;
            
            methods.xhr({
                url: `/v1/Login/GetLoginMethod`,
                setSurveyId: true,
                data: data,
                callback: (xhr) => {
                    let response = JSON.parse(xhr.responseText);
                    let actionObject = response.actionObject;
                    
                    response = apivalidation.validate(response, `getLoginMethod`);

                    if (response.action === `Redirect`) {
                        // If we redirect, we do need a loader to show until the redirect is finished
                        this.$store.commit(`changeGlobal`, {
                            variableName: `showLoader`,
                            newValue: true,
                        });
                        location.replace(response.actionObject.redirectUrl);
                    }

                    if (response.action !== `ShowError`) this.processSettingsFromServer(response);

                    // If this action is returned, it means the user has logged in by queryparameter. We can immediately start the survey
                    if (response.action === `ShowSurveyPage`) {
                        this.$store.commit(`addPageData`, {
                            page: actionObject,
                        });
                        this.$router.replace(`Questionnaire`);
                    } else if (response.action === `ShowError` || response.action === `SurveyEnd`) {
                        this.$router.replace({
                            name: `Endpage`,
                            params: {
                                title: actionObject.title,
                                message: actionObject.message || actionObject.endText,
                                reasonForEndpage: actionObject.reasonForEndpage,
                            },
                        });
                    } else if (response.action === `ShowStartPage`) {
                        this.$store.commit(`addSurveyInfo`, response);
                        this.$router.replace({
                            name: `Beginpage`,
                            params: {
                                linkText: response.actionObject.linkText,
                                text: response.actionObject.text,
                            },
                        });
                    }

                    let loginType = this.surveyInfo.loginType;

                    if (this.loginTypesThatRequireLoginScreen.includes(loginType)) {
                        this.$router.replace({
                            name: `Loginpage`,
                            params: { welcomeText: response.welcomeText },
                        });
                    } else if (loginType === `NoCheck` || loginType === `CookieCheck`) {
                        this.navigateToOtherPage({ route: `Questionnaire` });
                    }

                    this.$store.dispatch("loadText");
                },
            });
        },
        // Process the settings that are received from the server (such as layout and survey info)
        processSettingsFromServer: function (response) {
            let layoutValues = response.layoutSettings;

            this.$store.commit(`addLayoutValues`, response.layoutSettings);
            this.$store.commit(`addLanguages`, response.languages);
            this.$store.commit(`addSurveyInfo`, response);

            document.title = layoutValues.PageTitle;
            if (layoutValues.FaviconImagePath !== `` && layoutValues.FaviconImagePath !== null) methods.setFavicon(layoutValues.FaviconImagePath);

            this.loadCustomFonts([layoutValues.PageStyling, layoutValues.AnswerStyling, layoutValues.DescriptionStyling, layoutValues.QuestionStyling]);
        },
        // Load a custom font if the browser doesn't support this font itself
        loadCustomFonts: function (fonts) {
            fonts.forEach((font) => {
                // We don't have to load webfonts
                if (font.IsWebSafeFont) return;

                let url = ``;

                if (this.state.surveyInfo.surveyId !== null) {
                    url = `url(/v1/Fonts/Get?Id=${font.FontId}&surveyId=${this.state.surveyInfo.surveyId})`;
                } else {
                    url = `url(/v1/Fonts/GetPublicFont?fontId=${font.FontId})`;
                }

                let loadFont = new FontFace(font.FontFamilyPublicName, url);

                loadFont
                    .load()
                    .then(function (loaded_face) {
                        document.fonts.add(loaded_face);
                    })
                    .catch(function (error) {
                        console.warn(error);
                    });
            });
        },
        // Navigate to another view
        navigateToOtherPage: function (params) {
            let isWithCredentials = false;
            let data = params.data || {};
            let queryParams = this.state.surveyInfo.queryParams;

            data.surveyId = this.surveyInfo.surveyId;

            if (queryParams && Object.keys(queryParams).length > 0) data.queryParameters = queryParams;
            if (this.state.surveyInfo.loginType === `CookieCheck`) isWithCredentials = true;
            
            methods.xhr({
                url: this.state.surveyInfo.authenticationUrl,
                withCredentials: isWithCredentials,
                setSurveyId: true,
                data: data,
                callback: (xhr) => {
                    let response = JSON.parse(xhr.responseText);

                    response = apivalidation.validate(response, `navigateToOtherPage`);
                    apivalidation.validateQuestionObj(response.actionObject);

                    if (response.layoutSettings) this.processSettingsFromServer(response);

                    if (response.action === `ShowError` || response.action === `SurveyEnd`) {
                        this.$router.replace({
                            name: `Endpage`,
                            params: {
                                title: response.title,
                                message: response.actionObject.message || response.message,
                                reasonForEndpage: response.actionObject.reasonForEndpage,
                            },
                        });
                    } else if (response.action === `Redirect`) {
                        // If we redirect, we do need a loader to show until the redirect is finished
                        this.$store.commit(`changeGlobal`, {
                            variableName: `showLoader`,
                            newValue: true,
                        });
                        location.replace(response.actionObject.redirectUrl);
                    } else if (response.action === `ShowStartPage`) {
                        this.$store.commit(`addSurveyInfo`, response);
                        this.$router.replace({
                            name: `Beginpage`,
                            params: {
                                linkText: response.actionObject.linkText,
                                text: response.actionObject.text,
                            },
                        });
                    } else {
                        this.$store.commit(`addPageData`, {
                            page: response.actionObject,
                        });
                        this.$store.commit(`addSurveyInfo`, response);

                        this.$router.replace(params.route);
                    }
                },
            });
        },
        // Get the surveyId from the query parameter given.
        // For developers the parameter will be set in a cookie and the cookie will be used if no parameter is given
        getId: function (idType) {
            const idName = `${idType}Id`;
            const parameterId = methods.getParameterByName(idName.toLowerCase());
            let storageId;

            if (parameterId !== null) localStorage.setItem(idName, parameterId);

            if (process.env.VUE_APP_BUILD_MODE === `development` || process.env.VUE_APP_BUILD_MODE === `debug`) {
                storageId = localStorage.getItem(idName.toLowerCase());
            } else {
                storageId = sessionStorage.getItem(idName);
            }

            return parameterId || storageId;
        },
        // When the window is resized, update the window size in the state. This property can be used by other components to change their state
        windowResized: function (e) {
            this.$store.commit(`changeGlobal`, {
                variableName: `windowSize`,
                newValue: e.target.innerWidth,
            });
        },
        // The call that is used when someone wants to see a preview of their created layout
        loadPreview: function () {
            let queryParams = this.state.surveyInfo.queryParams;

            methods.xhr({
                url: `/v1/Layout/Preview?culture=${queryParams.culture}`,
                setSurveyId: true,
                data: {
                    layoutId: queryParams.layoutId,
                    surveyRemoteId: queryParams.surveyRemoteId,
                },
                callback: (xhr) => {
                    let response = JSON.parse(xhr.responseText);

                    response = apivalidation.validate(response, `preview`);

                    this.processSettingsFromServer(response.actionObject);
                    this.$store.commit(`addPageData`, {
                        page: response.actionObject,
                    });
                    this.$router.replace(`Questionnaire`);
                    this.$store.dispatch("loadText");
                },
            });
        },
        // The call that is used for providing a form where respondents can provide feedback or unsubscribe
        loadFeedbackPage: function () {
            const queryParams = this.state.surveyInfo.queryParams;
            let urlParams = ``;

            // If there are no queryparams, use the queryparams stored in localstorage
            if (queryParams.surveyId || queryParams.surveyid) urlParams = `?surveyid=${queryParams.surveyId || queryParams.surveyid}`;
            if (queryParams.respondentId || queryParams.respondentid)
                urlParams = `${urlParams}&respondentid=${queryParams.respondentId || queryParams.respondentid}`;

            methods.xhr({
                type: "GET",
                url: `/v1/Feedback/LoadPage` + urlParams,
                callback: (xhr) => {
                    let response = JSON.parse(xhr.responseText);
                    this.$store.commit(`addSurveyInfo`, response);
                    this.processSettingsFromServer(response.actionObject);
                    this.$store.dispatch(`loadText`);

                    this.$router.push({ path: "Feedbackpage", query: this.surveyInfo.queryParams });
                },
            });
        },
        // When the user refreshes a page while on the questionnaire, we want to reload the page that he/she was on
        loadRefreshedPage: function () {
            methods.xhr({
                url: `/v1/Questionnaire/Next`,
                data: {
                    surveyId: sessionStorage.getItem(`surveyId`),
                    respondentId: sessionStorage.getItem(`respondentId`),
                    sessionId: sessionStorage.getItem(`sessionId`),
                    pageId: sessionStorage.getItem(`pageId`),
                    token: sessionStorage.getItem(`token`),
                },
                setSurveyId: true,
                callback: (xhr) => {
                    let response = JSON.parse(xhr.responseText);
                    let actionObject = response.actionObject;

                    apivalidation.validateQuestionObj(actionObject);

                    this.$store.commit(`addSurveyInfo`, {
                        culture: response.culture || sessionStorage.getItem(`culture`),
                    });

                    this.$store.dispatch("loadText");
                    this.processSettingsFromServer(response);

                    this.$store.commit(`addSurveyInfo`, { actionObject });
                    this.$store.commit(`addServerMessages`, response.message);
                    this.$store.commit(`addPageData`, { page: actionObject });
                    this.$store.commit(`changeGlobal`, {
                        variableName: `isRefreshed`,
                        newValue: false,
                    });
                },
            });
        },
    },
    created: function () {
        let surveyId = this.getId(`survey`);
        let frameSurveyId = null;

        // Iframe start
        // If Survey is used in a Iframe for example feedback button in Surveyplatform
        // we get the surveyId set in the iframe.
        if (window.frameElement) frameSurveyId = window.frameElement.getAttribute(`data-guid`);        
        
        const respondentId = this.getId(`respondent`);
 
        if ((surveyId === null && window.frameElement) || frameSurveyId !== null) {
            this.$store.commit(`changeGlobal`, {
                variableName: `isWithinIframe`,
                newValue: true,
            });

            surveyId = frameSurveyId;
        }
        // Iframe ends
        this.$store.commit(`changeGlobal`, {
            variableName: `windowSize`,
            newValue: window.innerWidth,
        });
        this.$store.commit(`addSurveyInfo`, { surveyId: surveyId, respondentId: respondentId });

        this.$store.commit(`addSurveyInfo`, {
            queryParams: methods.querystringObj(),
        });

        if (window.frameElement) {
            this.$store.commit(`addSurveyInfo`, {
                queryParams: methods.querystringObj(window.frameElement.getAttribute(`data-query-strings`)),
            });
        }

        this.$store.commit(`changeGlobal`, {
            variableName: `isPreview`,
            newValue: this.state.surveyInfo.queryParams.layoutId !== undefined,
        });

        window.addEventListener(`resize`, this.windowResized);

        // A layoutId means the user wants to test the styling of a layout
        if (this.state.surveyInfo.queryParams.layoutId !== undefined) {
            this.loadPreview();
            return;
        }
 
        const action = this.state.surveyInfo.queryParams.action;
        if (action === `feedback` || action === `feedbackquestion` || action === `whatsappunsubscribe`) {
            this.loadFeedbackPage();
            return;
        }

        // If there is no surveyId, we can't do a survey.
        if (surveyId === null || surveyId === `null`) {
            this.$store.commit(`changeGlobal`, {
                variableName: `errorMessage`,
                newValue: `No survey id given`,
            });

            return;
        }

        // If the surveyId isn't a valid guid, we can't do a survey
        if (this.state.global.guidRegExp.test(surveyId) === false) {
            this.$store.commit(`changeGlobal`, {
                variableName: `errorMessage`,
                newValue: `Survey id is wrong format`,
            });

            return;
        }

        // If the user is refreshing the page(while on the questionnaire view) we have to reload the same questionnaire page
        if (this.state.global.isRefreshed) {
            this.loadRefreshedPage();
            return;
        } else {
            this.getLoginMethod();
        }
    },
};
</script>

<style lang="scss">
#app {
    height: 100%;
    display: table;
    width: 100%;
}

#error-message {
    z-index: 10000;
    position: fixed;
    top: 40%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 15px;

    strong {
        font-family: Verdana, Tahoma, sans-serif;
        color: #333;
        text-align: center;
        max-width: 1100px;
        box-shadow: 0 0 2px #000;
        background: white;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 19px;
        border: 1px solid #333;
    }
}

.fade-enter-active {
    transition: all 0.8s ease-out;
}

.fade-leave-active {
    pointer-events: none;
    transition: all 0.5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

[data-background] {
    background-color: var(--background-color);
    background-image: var(--background-image);
    background-attachment: fixed;
    background-repeat: no-repeat;
    background-position: center center;

    &[data-background-fill="true"] {
        background-size: cover;
    }

    &[data-layout-not-loaded] {
        background-image: none;
    }
}
</style>